/**
 * Pure translation and merge-decision logic for account sync.
 *
 * Deliberately dependency-free (no Supabase import, no network, no storage) so
 * it can be unit-tested directly. `syncService.js` is the impure layer that
 * calls these functions and does the actual network I/O.
 *
 * Privacy boundary lives here: `stripLocalOnlyFields` is the single place that
 * decides what never leaves the device. The per-question `questions` map
 * (which question, whether it needed hints, its mistake type) and the raw
 * `attempts` log are never part of what gets sent — only per-concept/topic/
 * level aggregates, XP, streak, stats and achievements.
 */

/** Fields that exist locally but must never be sent to the server. */
const LOCAL_ONLY_FIELDS = ['questions'];

export function stripLocalOnlyFields(progress) {
  const copy = { ...progress };
  for (const field of LOCAL_ONLY_FIELDS) delete copy[field];
  return copy;
}

/** Local progress shape -> Supabase `user_progress` row shape. */
export function toRemoteRow(progress, userId) {
  const stripped = stripLocalOnlyFields(progress);
  return {
    user_id: userId,
    schema_version: stripped.schemaVersion,
    xp: stripped.xp,
    rank_id: stripped.rankId,
    streak: stripped.streak,
    stats: stripped.stats,
    topics: stripped.topics,
    levels: stripped.levels,
    concepts: stripped.concepts,
    formulas: stripped.formulas,
    achievements: stripped.achievements,
    session_log: stripped.sessionLog,
    updated_at: stripped.updatedAt
  };
}

/**
 * Supabase `user_progress` row -> local progress shape.
 *
 * `questions` always comes back empty: the server never had it, and a pull
 * must never invent or clear the device's own per-question history.
 */
export function fromRemoteRow(row) {
  if (!row) return null;
  return {
    schemaVersion: row.schema_version,
    xp: row.xp || { total: 0, byDay: {} },
    rankId: row.rank_id || 'beginner',
    streak: row.streak || { current: 0, longest: 0, lastPracticeDay: null, days: {}, freezes: 1, frozenDays: [], lost: null },
    stats: row.stats || {},
    topics: row.topics || {},
    levels: row.levels || {},
    concepts: row.concepts || {},
    formulas: row.formulas || {},
    questions: {},
    achievements: row.achievements || {},
    sessionLog: Array.isArray(row.session_log) ? row.session_log : [],
    updatedAt: row.updated_at,
    createdAt: row.created_at || row.updated_at,
    lastActivityAt: row.updated_at
  };
}

/** Has this progress object seen any real activity worth protecting in a merge? */
export function hasRealProgress(progress) {
  if (!progress) return false;
  return Boolean(
    (progress.stats?.questionsAnswered ?? 0) > 0 ||
      (progress.sessionLog?.length ?? 0) > 0 ||
      Object.keys(progress.achievements || {}).length > 0 ||
      (progress.xp?.total ?? 0) > 0
  );
}

/** JSON with object keys in a fixed order, so equal data always gives equal text. */
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/** The part of a progress object that sync carries, without who owns it or when it was saved. */
function syncedContent(progress) {
  const { user_id: _owner, updated_at: _savedAt, ...content } = toRemoteRow(progress, null);
  return stableStringify(content);
}

/** Do the device and the account hold the same progress? (Timestamps and key order do not count.) */
export function sameProgress(local, remote) {
  if (!local || !remote) return false;
  return syncedContent(local) === syncedContent(remote);
}

/** Two timestamps for the same instant, however each side spells it ("Z" or "+00:00"). */
function sameInstant(a, b) {
  if (!a || !b) return false;
  return new Date(a).getTime() === new Date(b).getTime();
}

/**
 * Decide what to do with local vs. remote progress on sign-in.
 *
 * Whole-snapshot last-write-wins for the unambiguous cases (nothing to lose on
 * one side); only asks the learner when both sides have real, distinct
 * progress and picking wrong would silently discard something real.
 *
 * `link` is this device's record of when it last matched this account:
 * `{ local, remote }`, the `updatedAt` each side had at that moment (null on a
 * device that has never matched it). With it, a side that has not changed since
 * has nothing to lose, so the other side can be taken without asking. The
 * question is left for a first sign-in, and for the rare case where both sides
 * changed while apart.
 *
 * @returns {{action: 'use-local'|'use-remote'|'in-sync'|'ask-user', localUpdatedAt?, remoteUpdatedAt?}}
 */
export function decideMergeStrategy({ local, remote, link = null }) {
  if (!remote) return { action: 'use-local' };
  if (!hasRealProgress(local)) return { action: 'use-remote' };
  if (!hasRealProgress(remote)) return { action: 'use-local' };
  if (sameProgress(local, remote)) return { action: 'in-sync' };
  if (link) {
    const localChanged = !sameInstant(local.updatedAt, link.local);
    const remoteChanged = !sameInstant(remote.updatedAt, link.remote);
    if (!localChanged && remoteChanged) return { action: 'use-remote' };
    if (!remoteChanged) return { action: 'use-local' };
  }
  return {
    action: 'ask-user',
    localUpdatedAt: local.updatedAt,
    remoteUpdatedAt: remote.updatedAt
  };
}
